import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  servicerInstructionsApi,
  type ServicerInstructionFormat,
  type ServicerInstructionScope,
} from "@/api/servicerInstructions";
import { Button, Input, Label } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { extractErrorMessage } from "@/api/client";
import { servicerInstructionKeys } from "./servicerInstructionKeys";

/**
 * Payment System v3.0 (WS1) — Servicer Instruction Report generation wizard.
 * Scope picker → optional date range → format → Generate. POST returns the
 * persisted report; we redirect to the detail page on success.
 */
export function ServicerInstructionGeneratePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [scope, setScope] = useState<ServicerInstructionScope>("AllLive");
  const [loanId, setLoanId] = useState("");
  const [poolId, setPoolId] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [format, setFormat] = useState<ServicerInstructionFormat>("csv");
  const [error, setError] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () =>
      servicerInstructionsApi.generate({
        scope,
        format,
        rangeStart: rangeStart || null,
        rangeEnd: rangeEnd || null,
        loanId: scope === "Loan" ? loanId || null : null,
        poolId: scope === "Pool" ? poolId || null : null,
      }),
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: servicerInstructionKeys.list });
      navigate(`/servicer-instructions/${report.id}`);
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Generate Servicer Instruction Report"
        description="Collect every live disbursement the servicer executes (the inverse of a PIR), batched per period, and seal them onto a new report."
      />
      <form
        className="flex flex-col gap-space-4 max-w-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          generate.mutate();
        }}
      >
        <div className="flex flex-col gap-space-2">
          <Label>Scope</Label>
          <div className="flex gap-space-2">
            {(["AllLive", "Loan", "Pool"] as const).map((s) => (
              <Button
                key={s}
                type="button"
                variant={scope === s ? "primary" : "secondary"}
                onClick={() => setScope(s)}
              >
                {s === "AllLive" ? "All Live" : s}
              </Button>
            ))}
          </div>
        </div>

        {scope === "Loan" && (
          <div className="flex flex-col gap-space-2">
            <Label htmlFor="loan-id">Loan ID</Label>
            <Input
              id="loan-id"
              value={loanId}
              onChange={(e) => setLoanId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </div>
        )}
        {scope === "Pool" && (
          <div className="flex flex-col gap-space-2">
            <Label htmlFor="pool-id">Pool ID</Label>
            <Input
              id="pool-id"
              value={poolId}
              onChange={(e) => setPoolId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </div>
        )}

        <div className="flex gap-space-3">
          <div className="flex flex-col gap-space-2 flex-1">
            <Label htmlFor="range-start">Range start</Label>
            <Input
              id="range-start"
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-space-2 flex-1">
            <Label htmlFor="range-end">Range end</Label>
            <Input
              id="range-end"
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-space-2">
          <Label>Format</Label>
          <div className="flex gap-space-2">
            {(["csv", "pdf"] as const).map((f) => (
              <Button
                key={f}
                type="button"
                variant={format === f ? "primary" : "secondary"}
                onClick={() => setFormat(f)}
              >
                {f.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>

        {error && <p className="text-status-error text-sm">{error}</p>}

        <Button type="submit" loading={generate.isPending} disabled={generate.isPending}>
          Generate report
        </Button>
      </form>
    </>
  );
}
